import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';

export interface CameraHandle {
  pushAudio: (buffer: AudioBuffer) => void;
}

interface CameraViewProps {
  isActive: boolean;
  currentSubtitle: string;
  onFrame: (base64Data: string) => void;
  onAudioData: (data: Float32Array) => void;
  onRecordingComplete: (blob: Blob) => void;
}

export const CameraView = forwardRef<CameraHandle, CameraViewProps>(({ 
  isActive, 
  currentSubtitle,
  onFrame, 
  onAudioData,
  onRecordingComplete 
}, ref) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  
  // Audio Contexts
  // 1. Input Processing Context (for analysis)
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  
  // 2. Recording Mixing Context (for saving video)
  const recordingAudioContextRef = useRef<AudioContext | null>(null);
  const recordingDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const nextMixingTimeRef = useRef<number>(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const frameIntervalRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  
  // Refs for State (Fixes Stale Closures in Event Loops)
  const isActiveRef = useRef(isActive);
  
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [isMediaReady, setIsMediaReady] = useState(false);
  
  // Logo resources
  const logoImageRef = useRef<HTMLImageElement | null>(null);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    pushAudio: (sourceBuffer: AudioBuffer) => {
      // Only process if active and contexts are ready
      if (!isActiveRef.current || !recordingAudioContextRef.current || !recordingDestRef.current) return;
      
      try {
        const ctx = recordingAudioContextRef.current;
        
        // CRITICAL: We must clone the buffer because it belongs to a different AudioContext (the playback one).
        // Using it directly would throw an InvalidStateError or NotSupportedError.
        const newBuffer = ctx.createBuffer(
          sourceBuffer.numberOfChannels,
          sourceBuffer.length,
          sourceBuffer.sampleRate
        );
        
        for (let i = 0; i < sourceBuffer.numberOfChannels; i++) {
          newBuffer.copyToChannel(sourceBuffer.getChannelData(i), i);
        }

        const source = ctx.createBufferSource();
        source.buffer = newBuffer;
        source.connect(recordingDestRef.current);
        
        // Schedule sequentially to prevent overlaps/choppiness in recording
        const now = ctx.currentTime;
        if (nextMixingTimeRef.current < now) {
            nextMixingTimeRef.current = now;
        }
        
        source.start(nextMixingTimeRef.current);
        nextMixingTimeRef.current += newBuffer.duration;

      } catch (e) {
        console.error("Error pushing audio to recorder mixer", e);
      }
    }
  }));

  useEffect(() => {
    // Keep ref in sync with prop
    isActiveRef.current = isActive;
  }, [isActive]);

  useEffect(() => {
    // Preload logo for canvas drawing
    const img = new Image();
    const svgString = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
        <circle cx="50" cy="50" r="50" fill="#DC2626" />
        <path fill="black" transform="translate(5, 5) scale(0.9)" d="M30 95 C 25 75, 25 55, 40 40 C 45 35, 55 25, 65 32 C 75 36, 88 40, 92 48 C 94 54, 85 58, 80 60 C 75 62, 72 70, 70 80 C 68 88, 65 95, 60 95 Z" />
      </svg>
    `;
    const blob = new Blob([svgString], {type: 'image/svg+xml'});
    const url = URL.createObjectURL(blob);
    img.src = url;
    img.onload = () => {
      logoImageRef.current = img;
    };
  }, []);

  // 1. Initialize Camera on Mount (Live Preview)
  useEffect(() => {
    startMedia();
    return () => {
      stopMedia();
    };
  }, []);

  // 2. Handle Recording and AI Processing based on isActive
  useEffect(() => {
    if (isActive && isMediaReady && canvasRef.current) {
        // Start AI Frame Capture
        startFrameCapture();
        
        // Start Video Recording
        // Capture stream from the composited canvas
        const canvasEl = canvasRef.current as any;
        if (canvasEl.captureStream) {
            const canvasStream = canvasEl.captureStream(30); // 30 FPS
            startRecording(canvasStream);
        }
    } else if (!isActive) {
        // Stop Everything
        stopRecording();
        if (frameIntervalRef.current) {
            window.clearInterval(frameIntervalRef.current);
            frameIntervalRef.current = null;
        }
    }
  }, [isActive, isMediaReady]);

  const startMedia = async () => {
    setPermissionError(null);
    setIsMediaReady(false);

    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      setPermissionError("HTTPS Required: Camera access is blocked on insecure connections.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' }, 
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        }
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // Wait for video to actually start playing
        await videoRef.current.play().catch(e => console.error("Video play failed", e));
        
        // Set canvas dimensions immediately
        if (canvasRef.current) {
          canvasRef.current.width = videoRef.current.videoWidth;
          canvasRef.current.height = videoRef.current.videoHeight;
        }

        setIsMediaReady(true);
        // Start the visual render loop immediately for preview
        animationFrameRef.current = requestAnimationFrame(renderLoop);
      }

      setupAudioProcessing(stream);
      setupRecordingAudioMixer(stream);

    } catch (err: any) {
      console.error("Error accessing camera/mic:", err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
         setPermissionError("Camera permission denied.");
      } else {
         setPermissionError(`Failed to access media: ${err.message}`);
      }
    }
  };

  const stopMedia = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(console.error);
      audioContextRef.current = null;
    }
    
    if (recordingAudioContextRef.current) {
        recordingAudioContextRef.current.close().catch(console.error);
        recordingAudioContextRef.current = null;
    }

    if (frameIntervalRef.current) {
      window.clearInterval(frameIntervalRef.current);
      frameIntervalRef.current = null;
    }
  };

  const setupAudioProcessing = (stream: MediaStream) => {
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      let audioContext: AudioContext;
      try {
        audioContext = new AudioContextClass({ sampleRate: 16000 });
      } catch (e) {
        audioContext = new AudioContextClass();
      }
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      
      // CRITICAL FIX: Use isActiveRef.current instead of isActive closure
      processor.onaudioprocess = (e) => {
        if (!isActiveRef.current) return; // Check Ref for live state
        
        const inputData = e.inputBuffer.getChannelData(0);
        const dataCopy = new Float32Array(inputData);
        onAudioData(dataCopy);
      };
      
      source.connect(processor);
      const muteNode = audioContext.createGain();
      muteNode.gain.value = 0;
      processor.connect(muteNode);
      muteNode.connect(audioContext.destination);
    } catch (e) {
      console.error("Audio context setup failed", e);
    }
  };

  const setupRecordingAudioMixer = (stream: MediaStream) => {
      // This context mixes the microphone and the AI audio into a single stream for the MediaRecorder
      try {
          const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
          const ctx = new AudioContextClass();
          recordingAudioContextRef.current = ctx;
          nextMixingTimeRef.current = ctx.currentTime;

          const dest = ctx.createMediaStreamDestination();
          recordingDestRef.current = dest;

          // Add Microphone to mixer
          const micSource = ctx.createMediaStreamSource(stream);
          micSource.connect(dest);

      } catch (e) {
          console.error("Failed to setup recording mixer", e);
      }
  };

  const startRecording = (canvasStream: MediaStream) => {
    chunksRef.current = [];
    
    const mimeTypes = [
      'video/mp4',
      'video/webm;codecs=h264',
      'video/webm'
    ];
    
    let selectedMimeType = '';
    for (const type of mimeTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        selectedMimeType = type;
        break;
      }
    }

    try {
      let finalStream = canvasStream;
      
      // Mix in the Audio from our Mixing Context (Mic + AI)
      if (recordingDestRef.current) {
         const mixedAudioTracks = recordingDestRef.current.stream.getAudioTracks();
         if (mixedAudioTracks.length > 0) {
             // Create a new stream combining video from canvas and mixed audio
             finalStream = new MediaStream([
                 ...canvasStream.getVideoTracks(),
                 ...mixedAudioTracks
             ]);
         }
      } else if (streamRef.current) {
          // Fallback to just microphone if mixer failed
          const audioTracks = streamRef.current.getAudioTracks();
          if (audioTracks.length > 0) {
             finalStream.addTrack(audioTracks[0]);
          }
      }

      const recorder = new MediaRecorder(finalStream, {
        mimeType: selectedMimeType,
        videoBitsPerSecond: 2500000 
      });

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: selectedMimeType || 'video/webm' });
        if (blob.size > 0) {
          onRecordingComplete(blob);
        }
      };

      recorder.start(1000); 
      mediaRecorderRef.current = recorder;
    } catch (e) {
      console.error("Failed to start recording", e);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  };

  // Use a ref for subtitle to ensure the animation loop sees the latest text
  const subtitleRef = useRef(currentSubtitle);
  useEffect(() => {
    subtitleRef.current = currentSubtitle;
  }, [currentSubtitle]);

  const renderLoop = () => {
    if (!videoRef.current || !canvasRef.current) {
        animationFrameRef.current = requestAnimationFrame(renderLoop);
        return;
    }
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    if (ctx && video.readyState === video.HAVE_ENOUGH_DATA) {
       if (canvas.width !== video.videoWidth) canvas.width = video.videoWidth;
       if (canvas.height !== video.videoHeight) canvas.height = video.videoHeight;

       // 1. Video
       ctx.drawImage(video, 0, 0);

       // 2. Overlays (Only if ACTIVE/Recording)
       // CRITICAL FIX: Use isActiveRef.current instead of isActive closure
       if (isActiveRef.current) {
           // Logo (Burned in)
           if (logoImageRef.current) {
             const logoSize = Math.min(canvas.width, canvas.height) * 0.15;
             const padding = 20;
             ctx.shadowColor = 'rgba(0,0,0,0.5)';
             ctx.shadowBlur = 10;
             ctx.drawImage(logoImageRef.current, padding, padding, logoSize, logoSize);
             ctx.shadowBlur = 0;
             
             ctx.font = `italic 900 ${logoSize * 0.4}px sans-serif`;
             ctx.fillStyle = 'white';
             ctx.shadowColor = 'black';
             ctx.shadowBlur = 4;
             ctx.shadowOffsetX = 2;
             ctx.shadowOffsetY = 2;
             ctx.fillText("DOGLISH", padding + logoSize + 10, padding + (logoSize * 0.6));
           }

           // Subtitles (Burned in)
           const text = subtitleRef.current;
           if (text) {
             const fontSize = Math.min(canvas.width, canvas.height) * 0.05;
             ctx.font = `bold ${fontSize}px sans-serif`;
             ctx.textAlign = 'center';
             ctx.textBaseline = 'bottom';
             
             const maxWidth = canvas.width * 0.9;
             const x = canvas.width / 2;
             const y = canvas.height - (canvas.height * 0.25); 
             
             ctx.shadowColor = 'rgba(0,0,0,0.9)';
             ctx.shadowBlur = 8;
             ctx.shadowOffsetX = 0;
             ctx.shadowOffsetY = 2;
             ctx.fillStyle = '#FDE047'; 
             
             const words = text.split(' ');
             let line = '';
             const lines = [];
             
             for(let n = 0; n < words.length; n++) {
               const testLine = line + words[n] + ' ';
               const metrics = ctx.measureText(testLine);
               if (metrics.width > maxWidth && n > 0) {
                 lines.push(line);
                 line = words[n] + ' ';
               } else {
                 line = testLine;
               }
             }
             lines.push(line);

             const lineHeight = fontSize * 1.3;
             lines.reverse().forEach((l, i) => {
               ctx.lineWidth = fontSize * 0.08;
               ctx.strokeStyle = 'black';
               ctx.strokeText(l, x, y - (i * lineHeight));
               ctx.fillText(l, x, y - (i * lineHeight));
             });
           }
       }
    }
    
    animationFrameRef.current = requestAnimationFrame(renderLoop);
  };

  const startFrameCapture = () => {
    frameIntervalRef.current = window.setInterval(() => {
      // CRITICAL FIX: Use isActiveRef.current instead of isActive closure
      if (!videoRef.current || !isActiveRef.current) return;
      
      const video = videoRef.current;
      const smallCanvas = document.createElement('canvas');
      smallCanvas.width = video.videoWidth / 2;
      smallCanvas.height = video.videoHeight / 2;
      const ctx = smallCanvas.getContext('2d');
      
      if (ctx) {
        ctx.drawImage(video, 0, 0, smallCanvas.width, smallCanvas.height);
        const dataUrl = smallCanvas.toDataURL('image/jpeg', 0.6);
        const base64 = dataUrl.split(',')[1];
        onFrame(base64);
      }
    }, 500);
  };

  return (
    <div className="absolute inset-0 w-full h-full bg-gray-900 flex items-center justify-center overflow-hidden">
      {!isMediaReady && !permissionError && (
        <div className="text-white text-center z-40">
           <p className="text-sm font-medium animate-pulse">Initializing Camera...</p>
        </div>
      )}
      {permissionError && (
        <div className="absolute inset-0 flex items-center justify-center z-50 bg-black/90 text-white p-8">
            <p>{permissionError}</p>
        </div>
      )}

      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="hidden"
      />
      <canvas 
        ref={canvasRef} 
        className={`w-full h-full object-cover transition-opacity duration-500 ${isMediaReady ? 'opacity-100' : 'opacity-0'}`}
      />
    </div>
  );
});

CameraView.displayName = 'CameraView';